import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MatMomentDateModule
} from '@angular/material-moment-adapter';
import { EmptyValuePipe } from '@app/core/pipes/empty-value.pipe';
import { ShortNumberPipe } from '@app/core/pipes/short-number.pipe';
import { ErrorComponent } from '@app/shared/components/error/error.component';
import { NgxChartsModule } from '@swimlane/ngx-charts';

import { HeaderScriptsDirective } from '../core/directives/header-scripts';
import { SanitizeHtmlPipe } from '../core/pipes/sanitize-html.pipe';
import { PhoneNumberPipe } from './../core/pipes/phone-number.pipe';
import { BorrowerInformationComponent } from './../shared/components/borrower-information/borrower-information.component';
import { LoanCalculatorComponent } from './../shared/components/loan-calculator/loan-calculator.component';
import { PpiCoveragesComponent } from './../shared/components/ppi-coverages/ppi-coverages.component';
import { UnityDirectProductsComponent } from './../shared/components/products/unity-direct-products.component';
import { VehicleInformationComponent } from './../shared/components/vehicle-information/vehicle-information.component';
import { ActivityOverlayComponent } from './components/activity-overlay/activity-overlay.component';
import { AssociatedProductsComponent } from './components/associated-products/associated-products.component';
import { BundleCardComponent } from './components/bundle-card/bundle-card.component';
import { BundlesConfirmIneligibleDialog } from './components/bundles-confirm-ineligible-dialog/bundles-confirm-ineligible-dialog.component';
import { DynamicDialogComponent } from './components/dynamic-dialog/dynamic-dialog.component';
import { DynamicDialogService } from './components/dynamic-dialog/dynamic-dialog.service';
import { ErrorMessageComponent } from './components/error-message/error-message.component';
import { GapGraphComponent } from './components/gap-graph/gap-graph.component';
import { HeaderBarComponent } from './components/header-bar/header-bar.component';
import { ISAComponent } from './components/isa/isa.component';
import { ISACommonRepairsDocComponent } from './components/isa-common-repairs-doc/isa-common-repairs-doc.component';
import { LoaderComponent } from './components/loader/loader.component';
import { MmpComparisonComponent } from './components/mmp-comparison/mmp-comparison.component';
import { MmpGraphComponent } from './components/mmp-graph/mmp-graph.component';
import { PepGraphComponent } from './components/pep-graph/pep-graph.component';
import { PortalHeaderComponent } from './components/portal-header/portal-header.component';
import { SessionTimeoutComponent } from './components/session-timeout/session-timeout.component';
import { SessionTimeoutPlaceholderComponent } from './components/session-timeout-placeholder/session-timeout-placeholder.component';
import { SiteFooterComponent } from './components/site-footer/site-footer.component';
import { SliderComponent } from './components/sliders/sliders.component';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { UnityDirectHeaderBarComponent } from './components/unity-direct-header-bar/unity-direct-header-bar.component';
import { UnityDirectPanelActionsComponent } from './components/unity-direct-panel-actions/unity-direct-panel-actions.component';
import { UnityDirectPanelSummary } from './components/unity-direct-panel-summary/unity-direct-panel-summary.component';
import { VehicleTypeComponent } from './components/vehicle-type/vehicle-type.component';
import { ValuationCardComponent } from './components/vehicle-valuation-card/vehicle-valuation-card.component';
import { VehicleValuationDownloadComponent } from './components/vehicle-valuation-download/vehicle-valuation-download.component';
import { CompareRatesDialogComponent } from './components/compare-rates-dialog/compare-rates-dialog.component';
import { ValuationDialogComponent } from './vehicle-valuation/valuation-dialog.component';
import { MmpDownloadDialogComponent } from './components/mmp-download-dialog/mmp-download-dialog.component';
import { AlertsModule } from './components/alerts/alerts.module';
const materialModules = [
  MatFormFieldModule,
  MatAutocompleteModule,
  MatButtonModule,
  MatProgressSpinnerModule,
  MatCardModule,
  MatCheckboxModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatDialogModule,
  MatDividerModule,
  MatExpansionModule,
  MatInputModule,
  MatPaginatorModule,
  MatRadioModule,
  MatSelectModule,
  MatSliderModule,
  MatSnackBarModule,
  MatSortModule,
  MatTableModule,
  MatTabsModule,
  MatTooltipModule,
  MatIconModule,
  OverlayModule,
  MatMomentDateModule,
  MatChipsModule,
  MatSlideToggleModule
];

@NgModule({
  declarations: [
    HeaderBarComponent,
    UnityDirectHeaderBarComponent,
    UnityDirectPanelSummary,
    UnityDirectPanelActionsComponent,
    SiteFooterComponent,
    LoaderComponent,
    EmptyValuePipe,
    ErrorComponent,
    SessionTimeoutComponent,
    SessionTimeoutPlaceholderComponent,
    PhoneNumberPipe,
    SanitizeHtmlPipe,
    ErrorMessageComponent,
    DynamicDialogComponent,
    MmpComparisonComponent,
    MmpGraphComponent,
    GapGraphComponent,
    PepGraphComponent,
    ShortNumberPipe,
    ValuationCardComponent,
    VehicleValuationDownloadComponent,
    ActivityOverlayComponent,
    ValuationDialogComponent,
    SliderComponent,
    SpinnerComponent,
    PortalHeaderComponent,
    HeaderScriptsDirective,
    BundleCardComponent,
    VehicleTypeComponent,
    AssociatedProductsComponent,
    BundlesConfirmIneligibleDialog,
    LoanCalculatorComponent,
    BorrowerInformationComponent,
    VehicleInformationComponent,
    UnityDirectProductsComponent,
    PpiCoveragesComponent,
    ISAComponent,
    ISACommonRepairsDocComponent,
    MmpDownloadDialogComponent,
    CompareRatesDialogComponent
  ],
  imports: [
    CommonModule,
    NgxChartsModule,
    FormsModule,
    ReactiveFormsModule,
    materialModules,
    AlertsModule
  ],
  exports: [
    ReactiveFormsModule,
    FormsModule,
    materialModules,
    AlertsModule,
    MmpComparisonComponent,
    MmpGraphComponent,
    GapGraphComponent,
    PepGraphComponent,
    HeaderBarComponent,
    UnityDirectHeaderBarComponent,
    UnityDirectPanelSummary,
    UnityDirectPanelActionsComponent,
    ValuationCardComponent,
    ValuationDialogComponent,
    VehicleValuationDownloadComponent,
    SiteFooterComponent,
    LoaderComponent,
    EmptyValuePipe,
    ErrorComponent,
    ActivityOverlayComponent,
    SessionTimeoutComponent,
    SessionTimeoutPlaceholderComponent,
    DynamicDialogComponent,
    ShortNumberPipe,
    PhoneNumberPipe,
    SanitizeHtmlPipe,
    ErrorMessageComponent,
    SliderComponent,
    SpinnerComponent,
    PortalHeaderComponent,
    HeaderScriptsDirective,
    BundleCardComponent,
    VehicleTypeComponent,
    AssociatedProductsComponent,
    BundlesConfirmIneligibleDialog,
    LoanCalculatorComponent,
    BorrowerInformationComponent,
    VehicleInformationComponent,
    UnityDirectProductsComponent,
    PpiCoveragesComponent,
    ISAComponent,
    ISACommonRepairsDocComponent
  ],
  providers: [
    DynamicDialogService,
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: false } }
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SharedModule {}
